import { useEffect, useState } from "react";
import { CheckCircle2, CircleDashed, Loader2, MapPin } from "lucide-react";
import { getUserProgress } from "@/lib/api";
import type { AssignmentProgress, AuthUser, BlockProgress } from "@/lib/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Banner, Spinner } from "./ui";
import { useLanguage } from "@/i18n";
import type { Translations } from "@/i18n/en";
import { formatNumber } from "@/lib/format";

/** Percentage of blocks submitted, or null when there is nothing to divide by. */
function submittedPercent(blocks: BlockProgress): number | null {
  if (blocks.total === 0) return null;
  return Math.round((blocks.submitted / blocks.total) * 100);
}

/**
 * A three-segment bar: submitted, in progress, not started.
 *
 * Segments rather than one filled bar because "how much is done" is two
 * different questions here -- what is finished and what is underway -- and a
 * single percentage hides a surveyor who has started everything and finished
 * nothing, which is the state worth spotting.
 */
function ProgressBar({ blocks }: { blocks: BlockProgress }) {
  if (blocks.total === 0) {
    return <div className="h-1.5 w-full rounded-full bg-surface-sunken" />;
  }
  const pct = (n: number) => `${(n / blocks.total) * 100}%`;
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken">
      <div style={{ width: pct(blocks.submitted) }} className="bg-status-success" />
      <div style={{ width: pct(blocks.in_progress) }} className="bg-status-warning" />
    </div>
  );
}

function Counts({ blocks }: { blocks: BlockProgress }) {
  const { t } = useLanguage();
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs">
      <span className="flex items-center gap-1 text-status-success">
        <CheckCircle2 className="h-3 w-3" />
        {blocks.submitted} {t.assignmentProgressDialog.submittedLabel}
      </span>
      <span className="flex items-center gap-1 text-status-warning">
        <Loader2 className="h-3 w-3" />
        {blocks.in_progress} {t.assignmentProgressDialog.inProgressLabel}
      </span>
      <span className="flex items-center gap-1 text-ink-muted">
        <CircleDashed className="h-3 w-3" />
        {blocks.pending} {t.assignmentProgressDialog.notStartedLabel}
      </span>
    </div>
  );
}

/**
 * What one account has been given to survey, and how far through it is.
 *
 * Read-only, and fetched when opened rather than with the account list: the
 * list renders every account, and this is three aggregate queries per row that
 * nobody has asked to see yet.
 */
export default function AssignmentProgressDialog({
  user,
  onOpenChange,
}: {
  /** Null closes the dialog. */
  user: AuthUser | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, lang } = useLanguage();
  const [progress, setProgress] = useState<AssignmentProgress | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setProgress(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProgress(null);
    getUserProgress(user.id)
      .then((p) => {
        // Reopening on a different account while the first fetch is in flight
        // would otherwise show one account's progress under the other's name.
        if (!cancelled) setProgress(p);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t.assignmentProgressDialog.couldntLoadProgress);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const overall = progress ? submittedPercent(progress.blocks) : null;

  return (
    <Dialog open={user !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{user?.full_name || user?.username}</DialogTitle>
          <DialogDescription>
            {t.assignmentProgressDialog.assignmentProgress}
            {user?.username ? ` · ${user.username}` : ""}
          </DialogDescription>
        </DialogHeader>

        {error && <Banner tone="danger">{error}</Banner>}

        {loading && (
          <p className="flex items-center gap-2 py-6 text-xs text-ink-muted">
            <Spinner className="h-3.5 w-3.5" /> {t.assignmentProgressDialog.workingItOut}
          </p>
        )}

        {progress && !loading && (
          <div className="space-y-4">
            {/* An admin has no assignment to be part-way through -- it can
                reach everything, which is not the same as having been given
                everything to survey. Said in words rather than shown as 0 of
                0, which reads as an account with nothing to do. */}
            {progress.unrestricted ? (
              <p className="rounded border border-line bg-surface-sunken px-3 py-2 text-xs text-ink-secondary">
                {t.assignmentProgressDialog.adminNote}
              </p>
            ) : progress.blocks.total === 0 ? (
              <p className="rounded border border-line bg-surface-sunken px-3 py-2 text-xs text-status-text-critical">
                {t.assignmentProgressDialog.nothingAssignedYetPrefix}{" "}
                <span className="font-medium">{t.assignmentProgressDialog.assignmentWord}</span>{" "}
                {t.assignmentProgressDialog.nothingAssignedYetSuffix}
              </p>
            ) : (
              <>
                <div className="space-y-1.5">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs font-medium text-ink">
                      {t.assignmentProgressDialog.blocksAssigned(progress.blocks.total)}
                    </span>
                    <span className="tabular text-xs font-semibold text-ink">
                      {t.assignmentProgressDialog.percentDone(overall)}
                    </span>
                  </div>
                  <ProgressBar blocks={progress.blocks} />
                  <Counts blocks={progress.blocks} />
                </div>

                {/* Properties are the work inside those blocks. An imported
                    outline with no number is a property nobody has visited,
                    so this is the finer-grained answer to the same question. */}
                {progress.properties.total > 0 && (
                  <div className="rounded border border-line px-3 py-2">
                    <p className="text-2xs uppercase tracking-wider text-ink-muted">
                      {t.assignmentProgressDialog.propertiesInBlocksTitle}
                    </p>
                    <p className="mt-0.5 text-xs text-ink">
                      <span className="font-semibold">
                        {formatNumber(progress.properties.numbered, lang)}
                      </span>{" "}
                      {t.assignmentProgressDialog.numberedOfTotal(
                        formatNumber(progress.properties.numbered, lang),
                        formatNumber(progress.properties.total, lang)
                      )}
                      <span className="text-ink-muted">
                        {" "}
                        ·{" "}
                        {t.assignmentProgressDialog.stillToDo(
                          formatNumber(
                            progress.properties.total - progress.properties.numbered,
                            lang
                          )
                        )}
                      </span>
                    </p>
                  </div>
                )}

                {/* Only worth splitting out when there is more than one --
                    a single project would just repeat the totals above. */}
                {progress.by_project.length > 1 && (
                  <div className="space-y-2">
                    <p className="text-2xs uppercase tracking-wider text-ink-muted">
                      {t.assignmentProgressDialog.byProject}
                    </p>
                    {progress.by_project.map((entry) => (
                      <div key={entry.project_id} className="space-y-1">
                        <div className="flex items-baseline justify-between">
                          <span className="flex items-center gap-1.5 text-xs text-ink">
                            <MapPin className="h-3 w-3 text-ink-muted" />
                            {entry.project_name ?? `#${entry.project_id}`}
                          </span>
                          <span className="tabular text-2xs text-ink-muted">
                            {t.assignmentProgressDialog.blocksFraction(entry.blocks.submitted, entry.blocks.total)}
                          </span>
                        </div>
                        <ProgressBar blocks={entry.blocks} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
